import { Box, Button, Center, Group, SegmentedControl, Text, Tooltip } from "@mantine/core";
import { ISelectedGisMap } from "@mctypes/frontend.types";
import { TDimension } from "@mctypes/global.types";
import { IIssueBoardApi, IIssueViewEnum } from "@schemas/issueBoardSchema";
import { IIssueApi, ITableTemplate } from "@schemas/issueSchema";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ReactToPrint from "react-to-print";
import { CreateNewButton, DimensionSelector, ExcelExportDropdown, InfoCircle, MCLoader } from "../../../../components";
import McDropdownNew from "../../../../components/McDropdown/MCDropdownNew";
import ToggleTextExpand from "../../../../components/ToggleTextExpand";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { IssueViewEnum } from "../../../../mc-shared/enums";
import { DimensionEnums } from "../../../../mc-shared/sharedConstants";
import { getSelectedGisMapToLoadByDefault } from "../../../utils";
import { projectHasBotha2dAnd3dArcgisMap } from "../../admin/selectors";
import {
  ISSUE_DASHBOARD_LS_KEY,
  ISSUE_DASHBOARD_OPTIONS,
  ISSUE_KANBAN_GROUPING_LS_KEY,
  ISSUE_KANBAN_GROUPING_VIEWS,
  ISSUE_VIEWS,
  BIM_ISSUE_VIEW_PROJECT_NOS,
  IssueKanbanGroupingViewEnum,
} from "../issueConstants";
import { selectActiveTableTemplate } from "../issueSelectors";
import {
  setActiveDashboardInRedux,
  setIssueActiveViewInRedux,
  setIssueSelectedGisMapInRedux,
  setKanbanGroupingViewInRedux,
} from "../issueSlice";
import { getIssueBoardIdFromUrl, getIssueRegisterNameOption, getIssueUrlPath } from "../issueUtils";
import AddIssueModal from "./AddIssueModal";
import IssueApiButton from "./IssueApiButton";
import IssueBoardTemplates from "./IssueBoardTemplates/IssueBoardTemplates";
import IssueDocxTemplates from "./IssueDocxTemplates";

const IssueTabActionBar: React.FC<{
  setActiveTab: (tab: string) => void;
  issueBoard: IIssueBoardApi;
  setTableByTableTemplateCb: (template: ITableTemplate) => void;
  gridRef: React.RefObject<AgGridReact>;
  isLoading: boolean;
}> = ({ setActiveTab, issueBoard, setTableByTableTemplateCb, gridRef, isLoading }) => {
  const [addIssueModalOpen, setAddIssueModalOpen] = useState(false);
  const history = useHistory();

  const issueBoardId = getIssueBoardIdFromUrl(history.location.pathname);

  const dispatch = useAppDispatch();

  const project = useAppSelector((state) => state.adminReducer.project);

  const showDimensionSelector = useSelector(projectHasBotha2dAnd3dArcgisMap);

  const activeView = useAppSelector((state) => state.issueReducerNew.activeView);
  const activeTemplate = useAppSelector(selectActiveTableTemplate);
  const kanbanGroupingView = useAppSelector((state) => state.issueReducerNew.kanbanGroupingView);
  const selectedGisMap = useAppSelector((state) => state.issueReducerNew.selectedGisMap);
  const activeDashboard = useAppSelector((state) => state.issueReducerNew.activeDashboard);
  const isTaskActive: boolean = useAppSelector((s) => s.issueReducerNew.taskAndOrIssueArgs.includes("TASK"));
  const showBimIssueView = projectNoHasBimIssueView(project?.maconomy?.projectNo);
  React.useEffect(() => {
    if (selectedGisMap == null && project.arcgis?.groupId != null) {
      const _selectedGisMap = getSelectedGisMapToLoadByDefault(project.arcgis);

      dispatch(setIssueSelectedGisMapInRedux(_selectedGisMap));
    }
  }, []);

  const setGisDimension = (dimension: TDimension) => {
    let _selectedGisMap: ISelectedGisMap = {
      dimension: null,
      mapId: null,
    };
    if (dimension === DimensionEnums.TWODIMENSION) {
      _selectedGisMap = {
        dimension: DimensionEnums.TWODIMENSION,
        mapId: project.arcgis?.default2dMap,
      };
    }
    if (dimension === DimensionEnums.THEREEDIMENSION) {
      _selectedGisMap = {
        dimension: DimensionEnums.THEREEDIMENSION,
        mapId: project.arcgis?.default3dMap,
      };
    }
    dispatch(setIssueSelectedGisMapInRedux(_selectedGisMap));
  };

  const addIssueAndCloseModalAndOpenIssue = (issue: IIssueApi) => {
    setAddIssueModalOpen(false);
    history.push(getIssueUrlPath(history.location.pathname, issue._id));
  };

  const goToSettings = () => {
    setActiveTab("settings");
  };

  useEffect(() => {
    if (issueBoard != null) {
      const kanbanViewSetting = localStorage.getItem(
        `${ISSUE_KANBAN_GROUPING_LS_KEY}_${issueBoardId}`,
      ) as IssueKanbanGroupingViewEnum;

      const viewMode = determineKanbanGroupingView(issueBoard, kanbanViewSetting);
      dispatch(setKanbanGroupingViewInRedux(viewMode));
    }
  }, [issueBoard]);

  useEffect(() => {
    if (activeView === IssueViewEnum.BIM && !showBimIssueView) {
      dispatch(setIssueActiveViewInRedux(IssueViewEnum.TABLE));
    }
  }, [activeView, dispatch, showBimIssueView]);

  const excelExportColumns = (allColumns: boolean) => {
    let fileprefix = getIssueRegisterNameOption(issueBoard?.issueNameSetting, "plural") ?? "Issues";
    fileprefix = fileprefix.charAt(0).toUpperCase() + fileprefix.slice(1); //capitalize first letter
    const fileName = `${fileprefix}_${JSON.stringify(new Date())}.xlsx`;
    const columns = gridRef.current?.api
      .getColumns()
      .filter(
        (column) =>
          column.getColDef().type !== "metaColumn" &&
          column.getColDef().type !== "alwaysHidden" &&
          column.getColDef().context !== "METACOLUMN",
      );
    const filteredColumns = allColumns ? columns : columns.filter((column) => column.isVisible());
    if (filteredColumns.length < 1) return;
    gridRef.current!.api.exportDataAsExcel({
      fileName,
      sheetName: "Data",
      columnKeys: filteredColumns.map((col) => col.getColId()),
    });
  };

  const exportExcelAllColumns = () => {
    excelExportColumns(true);
  };

  const excelExportVisibleColumns = () => {
    excelExportColumns(false);
  };

  return (
    <Box w="100%" p="xxs">
      <Group align="center" gap="xs" wrap="nowrap" w="100%">
        <AddIssueModal
          toggle={() => setAddIssueModalOpen(false)}
          isOpen={addIssueModalOpen}
          issueBoardId={issueBoardId}
          onCreateItem={addIssueAndCloseModalAndOpenIssue}
          currentView={activeView}
        />
        <CreateNewButton
          size={"sm"}
          onClick={() => setAddIssueModalOpen(true)}
          tooltip={`Opprett ${getIssueRegisterNameOption(issueBoard?.issueNameSetting, "singular")?.toLowerCase()}`}
          disabled={activeView === "GIS"}
        />
        {activeView === "GIS" && <InfoCircle text="Klikk på kartet for å registrere" />}
        <IssueBoardTemplates gridRef={gridRef} />
        <IssueDocxTemplates gridRef={gridRef} />
        {isLoading === true && <MCLoader size={7} />} <Box flex={1} />
        {activeView === IssueViewEnum.TABLE && (
          <Group gap="xs" wrap="nowrap">
            <ExcelExportDropdown
              excelExportVisibleColumns={excelExportVisibleColumns}
              exportExcelAllColumns={exportExcelAllColumns}
            />

            <ReactToPrint
              trigger={() => (
                <Tooltip label="Tabellutskrift" withArrow offset={2} openDelay={200}>
                  <Button color={"mc-primary"} px="sm" variant="light" size="sm" id="tabellutskrift">
                    <i className="fa fa-print fa-fw" />
                  </Button>
                </Tooltip>
              )}
              content={() => document.querySelector<HTMLElement>("#myGrid")}
              onBeforeGetContent={() => {
                gridRef?.current?.api.setSideBarVisible(false);
              }}
              onAfterPrint={() => {
                gridRef?.current?.api.setSideBarVisible(true);
              }}
            />
            <ToggleTextExpand currentApp={"issues"} />
          </Group>
        )}
        {activeView === IssueViewEnum.KANBAN && (
          <Group gap="xs" align="center" wrap="nowrap">
            {kanbanGroupingView === IssueKanbanGroupingViewEnum.BUCKET && isTaskActive === true && (
              <Text
                size="xs"
                fw={700}
                px={6}
                py={4}
                style={{
                  backgroundColor: "#fff3cd",
                  color: "#856404",
                  border: "1px solid #ffeeba",
                  borderRadius: 4,
                }}
              >
                Aksjoner vises ikke for buckets
              </Text>
            )}
            <Text size="sm" c="dimmed" fw={600}>
              Gruppering
            </Text>
            <SegmentedControl
              aria-label="Velg grupperingsvisning"
              color="mc-primary"
              value={kanbanGroupingView}
              onChange={(val) => {
                localStorage.setItem(`${ISSUE_KANBAN_GROUPING_LS_KEY}_${issueBoardId}`, val);
                dispatch(setKanbanGroupingViewInRedux(val as IssueKanbanGroupingViewEnum));
              }}
              data={ISSUE_KANBAN_GROUPING_VIEWS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
          </Group>
        )}
        {activeView === IssueViewEnum.GIS && selectedGisMap != null && showDimensionSelector === true && (
          <DimensionSelector
            activeDimension={selectedGisMap.dimension}
            setActiveDimension={(dimension) => setGisDimension(dimension)}
          />
        )}
        {activeView === IssueViewEnum.DASHBOARD && (
          <>
            <McDropdownNew
              color="mc-blue"
              currentValue={activeDashboard != null ? activeDashboard : issueBoard?.dashboardSetting}
              options={ISSUE_DASHBOARD_OPTIONS}
              onChange={(_, newValue) => {
                localStorage.setItem(`${ISSUE_DASHBOARD_LS_KEY}_${issueBoardId}`, newValue);
                dispatch(setActiveDashboardInRedux(newValue));
              }}
            />
          </>
        )}
        <ViewToggler
          activeView={activeView}
          showBimIssueView={showBimIssueView}
          setActiveView={(view) => dispatch(setIssueActiveViewInRedux(view))}
        />
        <IssueApiButton />
      </Group>
    </Box>
  );
};

export default IssueTabActionBar;

const ViewToggler: React.FC<{
  activeView: IIssueViewEnum;
  showBimIssueView: boolean;
  setActiveView: (view: IIssueViewEnum) => void;
}> = ({ activeView, showBimIssueView, setActiveView }) => {
  const newViewVisibleByKey = useNewIssueViewVisibility();
  const issueViews = ISSUE_VIEWS.filter((view) => view.key !== IssueViewEnum.BIM || showBimIssueView);
  const data = issueViews.map((view) => ({
    value: view.key,
    label: (
      <Center inline style={{ gap: 4 }}>
        <i className={`fa fa-${view.icon} fa-fw"`} />
        <span>{view.label}</span>
        {newViewVisibleByKey[view.key] === true && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 18,
              padding: "0 6px",
              borderRadius: 9,
              background: "var(--mantine-color-yellow-4)",
              color: "var(--mantine-color-dark-8)",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            Ny
          </span>
        )}
      </Center>
    ),
  }));

  return (
    <SegmentedControl
      style={{ height: "fit-content" }}
      value={activeView}
      color="mc-primary"
      onChange={(val) => setActiveView(val as IssueViewEnum)}
      data={data}
    />
  );
};

const projectNoHasBimIssueView = (projectNo?: number): boolean =>
  projectNo != null && BIM_ISSUE_VIEW_PROJECT_NOS.some((enabledProjectNo) => enabledProjectNo === projectNo);

const NEW_ISSUE_VIEW_VISIBLE_DAYS = 15;
const NEW_ISSUE_VIEW_VISIBLE_MS = NEW_ISSUE_VIEW_VISIBLE_DAYS * 24 * 60 * 60 * 1000;
const getNewIssueViewSeenStorageKey = (viewKey: IIssueViewEnum) => `issueView:${viewKey}:firstSeenAsNew`;

const useNewIssueViewVisibility = (): Partial<Record<IIssueViewEnum, boolean>> => {
  const [visibleByKey, setVisibleByKey] = useState<Partial<Record<IIssueViewEnum, boolean>>>({});

  useEffect(() => {
    const now = Date.now();
    const nextVisibleByKey: Partial<Record<IIssueViewEnum, boolean>> = {};

    for (const view of ISSUE_VIEWS) {
      if (view.showAsNew !== true) continue;

      const storageKey = getNewIssueViewSeenStorageKey(view.key);
      const storedFirstSeen = localStorage.getItem(storageKey);
      const firstSeen = storedFirstSeen != null ? Number(storedFirstSeen) : now;

      if (storedFirstSeen == null || !Number.isFinite(firstSeen)) {
        localStorage.setItem(storageKey, String(now));
      }

      nextVisibleByKey[view.key] = now - firstSeen < NEW_ISSUE_VIEW_VISIBLE_MS;
    }

    setVisibleByKey(nextVisibleByKey);
  }, []);

  return visibleByKey;
};

const determineKanbanGroupingView = (
  board: IIssueBoardApi,
  localStorageValue: IssueKanbanGroupingViewEnum,
): IssueKanbanGroupingViewEnum => {
  if (board?.useBucketsAsWorkflow === true) {
    return IssueKanbanGroupingViewEnum.BUCKET;
  }

  if (localStorageValue != null) {
    const enumValues = Object.values(IssueKanbanGroupingViewEnum);

    if (enumValues.includes(localStorageValue)) {
      return localStorageValue;
    }
  }

  if (board?.buckets.length > 1) {
    return IssueKanbanGroupingViewEnum.BUCKET;
  }

  return IssueKanbanGroupingViewEnum.STATUS;
};
